import React, { useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  FlatList,
  Text,
  TouchableOpacity,
  Platform,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import SiteEvalCard from "./SiteEvalCard";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

interface SiteEvalCardViewProps {
  data: any[];
  columns: any[];
  onRowClick: (rowData: any, colIdx?: number) => void;
  onToggleChange?: (id: number, value: boolean) => void;
  isLoading?: boolean;
  activeItemsData?: any[];
  isActiveItemsTable?: boolean;
}

const SiteEvalCardView: React.FC<SiteEvalCardViewProps> = ({
  data,
  columns,
  onRowClick,
  onToggleChange,
  isLoading = false,
  activeItemsData = [],
  isActiveItemsTable = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 9;

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / rowsPerPage));

  // Ensure currentPage doesn't exceed totalPages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Get paginated data
  const paginatedData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Wrapper function to ensure colIdx is passed correctly
  const handleCardRowClick = (rowData: any, colIdx?: number) => {
    onRowClick(rowData, colIdx);
  };

  // Card separator
  const CardSeparator = () => <View style={{ height: 8 }} />;

  // Render pagination controls (used as FlatList footer so it scrolls into view)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <View style={[styles.paginationContainer, isDark && { backgroundColor: colors.background.surface, borderTopColor: colors.border.divider }]}>
        <View style={[styles.paginationBox, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
          {/* Left Arrow Cell */}
          <TouchableOpacity
            disabled={currentPage === 1}
            onPress={() => setCurrentPage(Math.max(1, currentPage - 1))}
            style={[
              styles.paginationCell,
              styles.paginationArrowCell,
              currentPage === 1 && styles.paginationCellDisabled,
            ]}
          >
            <MaterialIcons
              name="chevron-left"
              size={16}
              color={currentPage === 1 ? "#A0A0A0" : isDark ? colors.brand.primary : "#005CBC"}
            />
          </TouchableOpacity>

          {/* Current Page Cell (blue) */}
          <View style={[styles.paginationCell, styles.paginationCurrentCell]}>
            <Text style={styles.paginationCurrentText}>{currentPage}</Text>
          </View>

          {/* Next Page Cell (light) */}
          <View style={[styles.paginationCell, styles.paginationNextCell, isDark && { backgroundColor: colors.background.elevated }]}>
            <Text style={[styles.paginationNextText, isDark && { color: colors.brand.primary }]}>
              {currentPage < totalPages ? currentPage + 1 : currentPage}
            </Text>
          </View>

          {/* Right Arrow Cell */}
          <TouchableOpacity
            disabled={currentPage >= totalPages}
            onPress={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            style={[
              styles.paginationCell,
              styles.paginationArrowCell,
              currentPage >= totalPages && styles.paginationCellDisabled,
            ]}
          >
            <MaterialIcons
              name="chevron-right"
              size={16}
              color={currentPage >= totalPages ? "#A0A0A0" : isDark ? colors.brand.primary : "#005CBC"}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Cards List */}
      <FlatList
        data={isLoading ? [] : paginatedData}
        keyExtractor={(item, index) =>
          item.ID?.toString() || item.id?.toString() || index.toString()
        }
        renderItem={({ item }) => (
          <SiteEvalCard
            item={item}
            columns={columns}
            onRowClick={handleCardRowClick}
            onToggleChange={onToggleChange}
            activeItemsData={activeItemsData}
            isActiveItemsTable={isActiveItemsTable}
          />
        )}
        ItemSeparatorComponent={CardSeparator}
        showsVerticalScrollIndicator={true}
        scrollEnabled={true}
        removeClippedSubviews={false}
        maxToRenderPerBatch={15}
        initialNumToRender={10}
        windowSize={15}
        contentContainerStyle={[styles.listContent, { paddingBottom: 120 }]}
        ListFooterComponent={renderPagination()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "transparent",
  },
  resultsContainer: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: "transparent",
  },
  resultsText: {
    fontSize: 13,
    color: "#4B4B4B",
    textAlign: "left",
    fontFamily: FontFamily.regular,
    fontWeight: "500",
  },
  listContent: {
    paddingVertical: 8,
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "#FFFFFF",
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
  },
  paginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  paginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  paginationArrowCell: {
    backgroundColor: "transparent",
  },
  paginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  paginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  paginationCellDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 13,
    color: "#005CBC",
    fontWeight: "600",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  paginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  paginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
});

export default SiteEvalCardView;
